import { Component } from "react";
import { ColumnConfig, SortDirections, numberSort, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { SearchBox } from "@app/shared/SearchBox";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ServicingAnalysisGranularityEnum, ServicingCountTypeEnum, ServicingMinimumTypeEnum, TemplateServicingCriteria } from "@app/templates/templateModels";
import TemplateService from "@app/templates/templateService";

interface ServicingCriteriaPanelProps {
  className?: string;
  templateCollectionId: number;
  templateId: string;
  servicingAnalysisGranularity: ServicingAnalysisGranularityEnum;
  servicingCountBy: ServicingCountTypeEnum;
  servicingMinimumType: ServicingMinimumTypeEnum;
  specialtyGroupCode: string;
  specialtyGroupName: string;
}

interface ServicingCriteriaPanelState {
  filteredRecords: ServicingCriteriaTableData[];
  isOpen: boolean;
  loadStatus: LoadStatus;
  offset: number;
  search: string;
  skip: number;
  sortedRecords: ServicingCriteriaTableData[];
  totalCount: number;
}

interface ServicingCriteriaTableData {
  [wafflesKeyColumnName]: string;
  countyName: string;
  geographyDescription: string;
  servicingMinimumCount: number;
  servicingRatio: string;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  stateName: string;
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

export default class ServicingCriteriaPanel extends Component<ServicingCriteriaPanelProps,
  ServicingCriteriaPanelState> {
  private readonly batchCount: number = 4000;
  private readonly templateService = new TemplateService();
  private readonly take = 100;
  constructor(props: ServicingCriteriaPanelProps) {
    super(props);

    this.state = {
      filteredRecords: [],
      isOpen: false,
      loadStatus: "shouldLoad",
      offset: 0,
      search: "",
      skip: 0,
      sortedRecords: [],
      totalCount: 0,
    };
  }

  public render() {
    const initialLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const shouldRenderResults = this.state && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading");
    const noResultsOrError = this.state && (this.state.loadStatus === "empty" || this.state.loadStatus === "error");
    const shouldShowBatchCount = !!this.state && (this.state.isOpen && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading"));
    const totalRecordsShown = (this.take + this.state.skip) > this.state.filteredRecords.length ? this.state.filteredRecords.length : (this.take + this.state.skip);
    const title = `Servicing Criteria ${shouldShowBatchCount ? ` (${totalRecordsShown} of ${this.state.filteredRecords.length})` : ""}`;

    return (
      <AccordionContainer className={this.props.className}>
        <Accordion
          defaultOpen={false}
          title={title}
          onPanelWillOpen={initialLoading ? async () => await this.buildServicingCriteria() : null}
          onPanelDidOpen={this.handleOnOpen}
          onPanelDidClose={this.handleOnClose}
          testId="servicing-criteria-panel__servicing-criteria-accordion"
        >
          {(initialLoading || null) && (<h3 className="text-muted"><i className="fa fa-spinner fa-spin" />&nbsp;Loading servicing criteria...</h3>)}
          {(shouldRenderResults || null) && (this.renderServicingCriteriaTable())}
          {(shouldRenderResults || null) && (this.renderLoadMoreControls())}
          {(noResultsOrError || null) && (<h3 className="text-muted">No servicing criteria found.</h3>)}
        </Accordion>
      </AccordionContainer>
    );
  }

  private handleOnClose = () => {
    this.setState({ isOpen: false });
  }

  private handleOnOpen = () => {
    this.setState({ isOpen: true });
  }

  private handleCountySsaSearch(value: string) {
    this.setState((prevState) => ({
      ...prevState,
      filteredRecords: this.state.sortedRecords.filter(record => record.geographyDescription.includes(value)),
      search: value,
      skip: 0
    }));
  }

  private buildServicingCriteria = async () => {
    try {
      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad"
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : null;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        const ServicingCriteriaResult = await this.templateService.getTemplateSpecialtyGroupServicingCriteria(
          this.props.templateCollectionId,
          this.props.templateId,
          this.props.specialtyGroupCode,
          this.state.offset,
          this.batchCount,
        );

        let loadStatus: LoadStatus = "error";
        let records: ServicingCriteriaTableData[] = [];

        if (ServicingCriteriaResult !== null && ServicingCriteriaResult.criteria !== null) {
          if (ServicingCriteriaResult.criteria.length > 0) {
            loadStatus = "loaded";
            records = [...this.state.sortedRecords, ...this.buildTableData(ServicingCriteriaResult.criteria)];
          } else {
            loadStatus = "empty";
          }
        }

        this.setState((prevState) => ({
          ...prevState,
          ...ServicingCriteriaResult,
          filteredRecords: records,
          loadStatus,
          offset: prevState.offset + ServicingCriteriaResult.batchCount,
          sortedRecords: records,
        }));
      }
    } catch (ex) {
      this.setState((prevState): ServicingCriteriaPanelState => ({
        ...prevState,
        loadStatus: "error",
        sortedRecords: [],
      }));
    }
  }

  private renderLoadMoreControls = () => {
    const haveMoreToLoad = this.state.offset < this.state.totalCount;
    const partialLoading = haveMoreToLoad && this.state.loadStatus === "partialLoading";

    return (haveMoreToLoad || null) && (
      <div className="text-center">
        <h3 className="mtb2-imp text-muted"><i className={`fa fa-spinner fa-spin ${partialLoading ? "visible" : "invisible"}`} /></h3>
        <QESButton qesTestId="servicing-criteria-panel_load-more" onClick={async () => await this.buildServicingCriteria()} disabled={this.state.loadStatus !== "loaded"}>
          Load More
        </QESButton>
      </div>
    );
  }

  private buildTableData = (records: TemplateServicingCriteria[]): ServicingCriteriaTableData[] => {
    /* oxlint-disable sort-keys */
    return records.map((record) => ({
      [wafflesKeyColumnName]: record.geographyDescription,
      specialtyGroupCode: this.props.specialtyGroupCode,
      specialtyGroupName: this.props.specialtyGroupName,
      stateName: record.stateName,
      geographyDescription: record.geographyDescription,
      countyName: record.countyName,
      servicingMinimumCount: record.servicingMinimumCount,
      servicingRatio: record.servicingRatio,
    }));
    /* oxlint-enable sort-keys */
  }


  private renderServicingCriteriaTable = () => {
    const paginationProps: WafflesPaginationProps = {
      changePage: (pageNumber: number) => { this.setState({ skip: (pageNumber - 1) * this.take }); },
      recordsPerPage: this.take,
      skip: this.state.skip,
      totalRecords: this.state.filteredRecords.length,
    }
    return (
      <>
        <div className="col-12 col-lg-8 ps-0">
          <SearchBox
            descriptiveText={`This will search each County SSA.`}
            fieldsetClass="d-flex mt-0"
            inputLabel="County SSA Search"
            onSubmit={(value: string) => { this.handleCountySsaSearch(value); }}
            testId="servicing-criteria__search"
          />
        </div>
        <Waffles<ServicingCriteriaTableData>
          columns={this.getColumnConfig()}
          customSort={this.defaultSort}
          data={this.state.filteredRecords.slice(this.state.skip, this.state.skip + this.take)}
          enablePagination={true}
          fullWidth={true}
          paginationProps={paginationProps}
          testId="servicing-criteria-panel"
        />
      </>
    );
  }

  private getColumnConfig = (): ColumnConfig => {
    let displayCountyName = false;
    let displayStateName = false;
    let geographyTypeDescription: string = null;

    switch (this.props.servicingAnalysisGranularity) {
      case ServicingAnalysisGranularityEnum.County:
        geographyTypeDescription = "County SSA";
        displayCountyName = true;
        displayStateName = true;
        break;
      case ServicingAnalysisGranularityEnum.State:
        geographyTypeDescription = "State";
        displayStateName = true;
        break;
      case ServicingAnalysisGranularityEnum.Region:
        geographyTypeDescription = "Region";
        break;
    }

    return {
      "countyName": {
        displayText: "County",
        hidden: !displayCountyName,
        testId: "county",
      },
      "geographyDescription": {
        displayText: geographyTypeDescription,
        testId: "geography-description",
      },
      "servicingMinimumCount": {
        displayText: `Min ${this.props.servicingCountBy !== "unknown" ? this.props.servicingCountBy : ""}`,
        hidden: this.props.servicingMinimumType !== ServicingMinimumTypeEnum.MinimumProviders,
        testId: "servicing-minimum-count",
      },
      "servicingRatio": {
        displayText: "Servicing Ratio",
        hidden: this.props.servicingMinimumType !== ServicingMinimumTypeEnum.Ratio,
        testId: "servicing-ratio",
      },
      "specialtyGroupCode": {
        displayText: "Code",
        noSort: true,
        testId: "specialty-group-code",
      },
      "specialtyGroupName": {
        displayText: "Name",
        noSort: true,
        testId: "specialty-group-name",
      },
      "stateName": {
        displayText: "State",
        hidden: !displayStateName,
        testId: "state",
      },
    };
  }

  private defaultSort = (records: ServicingCriteriaTableData[], columnKey: keyof ServicingCriteriaTableData, sortDirection: SortDirections) => {
    const directionMultiplier = sortDirection === SortDirections.Asc ? 1 : -1;
    switch (columnKey) {
      case "servicingMinimumCount":
        return numberSort(records, columnKey, directionMultiplier);
      case "specialtyGroupCode":
      case "specialtyGroupName":
      case "stateName":
      case "geographyDescription":
      case "countyName":
      case "servicingRatio":
        return records.sort((a, b) => (a[columnKey] > b[columnKey] ? 1 : -1) * directionMultiplier);
    }
  }
}
